import React, { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

function ParticlesLayer({ count = 28 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  
  const cfg = theme?.custom?.layers?.particle ?? {};

  const colorA = cfg.colorA ?? '#a855f7';
  const colorB = cfg.colorB ?? '#ec4899';
  const blur = cfg.blur ?? 6;
  const boxShadow = cfg.boxShadow ?? '0 0 0 1px rgba(255,255,255,.08), 0 0 24px rgba(168,85,247,.25)';
  const sizeMin = cfg.sizeMin ?? 6;
  const sizeMax = cfg.sizeMax ?? 20;
  const floatMin = cfg.floatMin ?? 8;
  const floatMax = cfg.floatMax ?? 18;

  // Reduce particle count on mobile/tablet for better performance
  const optimizedCount = useMemo(() => {
    if (prefersReducedMotion) return 0;
    if (isMobile) return Math.floor(count * 0.3); // 30% on mobile
    if (isTablet) return Math.floor(count * 0.6); // 60% on tablet
    return count; // Full count on desktop
  }, [count, isMobile, isTablet, prefersReducedMotion]);

  const particles = useMemo(
    () =>
      Array.from({ length: optimizedCount }).map((_, i) => ({
        id: i,
        size: Math.round(sizeMin + Math.random() * (sizeMax - sizeMin)),
        left: Math.random() * 100,
        top: Math.random() * 100,
        dur: floatMin + Math.random() * (floatMax - floatMin),
        delay: Math.random() * 6
      })),
    [optimizedCount, sizeMin, sizeMax, floatMin, floatMax]
  );

  // Don't render if reduced motion is preferred or no particles
  if (prefersReducedMotion || optimizedCount === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -40, opacity: [0, 0.8, 0] }}
          transition={{ 
            duration: p.dur, 
            repeat: Infinity, 
            delay: p.delay,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            borderRadius: 999,
            filter: `blur(${isMobile ? blur * 0.7 : blur}px)`,
            background: `linear-gradient(135deg, ${colorA}, ${colorB})`,
            boxShadow,
            willChange: 'transform, opacity',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
      ))}
    </div>
  );
}

export default memo(ParticlesLayer);



// import { useEffect, useRef } from 'react';
// import { Box } from '@mui/material';
// import styles from '../../styles/helpers.module.css';

// const ParticlesLayer = () => {
//   const containerRef = useRef(null);

//   useEffect(() => {
//     const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
//     if (prefersReducedMotion) return;

//     const container = containerRef.current;
//     if (!container) return;

//     const particleCount = 50;
//     const particles = [];

//     for (let i = 0; i < particleCount; i++) {
//       const particle = document.createElement('div');
//       particle.className = styles.particle;
//       particle.style.left = `${Math.random() * 100}%`;
//       particle.style.top = `${Math.random() * 100}%`;
//       particle.style.animationDelay = `${Math.random() * 15}s`;
//       particle.style.animationDuration = `${10 + Math.random() * 10}s`;
//       container.appendChild(particle);
//       particles.push(particle);
//     }

//     return () => {
//       particles.forEach((p) => p.remove());
//     };
//   }, []);

//   return (
//     <Box
//       ref={containerRef}
//       sx={{
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         width: '100%',
//         height: '100%',
//         pointerEvents: 'none',
//         zIndex: 0,
//         overflow: 'hidden',
//       }}
//     />
//   );
// };

// export default ParticlesLayer;

